'use client';

import { useEffect, useRef, useState } from 'react';

interface BlogTransitionOverlayProps {
  clickPosition: { x: number; y: number };
  onComplete: () => void;
}

export default function BlogTransitionOverlay({
  clickPosition,
  onComplete,
}: BlogTransitionOverlayProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    // Check for prefers-reduced-motion
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);

    const handleChange = (e: MediaQueryListEvent) => {
      setPrefersReducedMotion(e.matches);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    if (prefersReducedMotion) {
      // Simple fade animation for reduced motion
      let opacity = 0;
      const fadeIn = () => {
        opacity += 0.05;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = `rgba(3, 27, 58, ${opacity})`;
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        if (opacity < 1) {
          requestAnimationFrame(fadeIn);
        } else {
          setTimeout(onComplete, 100);
        }
      };
      fadeIn();
      return;
    }

    // Full cinematic animation
    const startTime = Date.now();
    const duration = 600;
    const particles: Array<{
      x: number;
      y: number;
      vx: number;
      vy: number;
      size: number;
      opacity: number;
    }> = [];

    // Create star particles
    for (let i = 0; i < 50; i++) {
      const angle = (Math.PI * 2 * i) / 50;
      const speed = Math.random() * 3 + 2;
      particles.push({
        x: clickPosition.x,
        y: clickPosition.y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        size: Math.random() * 3 + 1,
        opacity: 1,
      });
    }

    let animationFrameId: number;

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Background fade
      ctx.fillStyle = `rgba(3, 27, 58, ${progress * 0.95})`;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Expanding ring
      const ringRadius = progress * Math.max(canvas.width, canvas.height) * 1.5;
      const ringGradient = ctx.createRadialGradient(
        clickPosition.x,
        clickPosition.y,
        Math.max(0, ringRadius - 50),
        clickPosition.x,
        clickPosition.y,
        ringRadius
      );
      ringGradient.addColorStop(0, 'rgba(0, 184, 255, 0)');
      ringGradient.addColorStop(0.5, `rgba(0, 184, 255, ${0.3 * (1 - progress)})`);
      ringGradient.addColorStop(1, 'rgba(0, 184, 255, 0)');

      ctx.beginPath();
      ctx.arc(clickPosition.x, clickPosition.y, ringRadius, 0, Math.PI * 2);
      ctx.fillStyle = ringGradient;
      ctx.fill();

      // Pulsing center glow
      const glowRadius = 100 + progress * 200;
      const glowGradient = ctx.createRadialGradient(
        clickPosition.x,
        clickPosition.y,
        0,
        clickPosition.x,
        clickPosition.y,
        glowRadius
      );
      glowGradient.addColorStop(0, `rgba(22, 197, 95, ${0.6 * (1 - progress)})`);
      glowGradient.addColorStop(0.5, `rgba(0, 184, 255, ${0.4 * (1 - progress)})`);
      glowGradient.addColorStop(1, 'rgba(0, 184, 255, 0)');

      ctx.beginPath();
      ctx.arc(clickPosition.x, clickPosition.y, glowRadius, 0, Math.PI * 2);
      ctx.fillStyle = glowGradient;
      ctx.fill();

      // Star particles
      particles.forEach((particle) => {
        particle.x += particle.vx * (1 + progress * 2);
        particle.y += particle.vy * (1 + progress * 2);
        particle.opacity = 1 - progress;

        // Draw particle with glow
        const particleGradient = ctx.createRadialGradient(
          particle.x,
          particle.y,
          0,
          particle.x,
          particle.y,
          particle.size * 3
        );
        particleGradient.addColorStop(0, `rgba(255, 255, 255, ${particle.opacity})`);
        particleGradient.addColorStop(0.5, `rgba(0, 184, 255, ${particle.opacity * 0.6})`);
        particleGradient.addColorStop(1, 'rgba(0, 184, 255, 0)');

        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size * 3, 0, Math.PI * 2);
        ctx.fillStyle = particleGradient;
        ctx.fill();

        // Bright center
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${particle.opacity})`;
        ctx.fill();
      });

      if (progress < 1) {
        animationFrameId = requestAnimationFrame(animate);
      } else {
        setTimeout(onComplete, 100);
      }
    };

    animate();

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [clickPosition, onComplete, prefersReducedMotion]);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 z-[9999] pointer-events-none"
      style={{ background: 'transparent' }}
    />
  );
}
